import { EntityType, Locale } from '@frontend/common';
import { ProductCategoryClient } from '@frontend/group/api';
import { IGroup, NodeType } from '@frontend/group/types';
import { ProductClient } from '@frontend/product/api';
import { IProduct } from '@frontend/product/types';
import { useAccountRepository } from '@frontend/repository';
import { useEffect, useState } from 'react';

export interface CategoryNode {
    category: IGroup;
    subcategories: CategoryNode[];
    products: IProduct[];
    isLoading: boolean;
    subcategoriesCount: number;
    productsCount: number;
}

export const useDirectoryView = () => {
    const {
        store: { currentAccount }
    } = useAccountRepository();
    const [categories, setCategories] = useState<CategoryNode[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (!currentAccount) return;

        const fetchRootCategories = async () => {
            setIsLoading(true);
            try {
                const response = await ProductCategoryClient.fetchProductCategories({
                    account_id: currentAccount,
                    node_type: NodeType.ROOT
                });

                const rootCategories: CategoryNode[] = await Promise.all(
                    response.results.map(async (category) => {
                        const members = await ProductCategoryClient.fetchProductCategoryMembers({
                            group_id: category.id
                        });

                        const subcategoriesCount = members.results.filter((m) => m.entity_type === EntityType.PRODUCT_CATEGORY).length;
                        const productsCount = members.results.filter((m) => m.entity_type === EntityType.PRODUCT).length;

                        return {
                            category,
                            subcategories: [],
                            products: [],
                            isLoading: false,
                            subcategoriesCount,
                            productsCount
                        };
                    })
                );

                setCategories(rootCategories);
            } catch (error) {
                console.error('Error fetching root categories:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchRootCategories();
    }, [currentAccount]);

    const findNodeByPath = (nodes: CategoryNode[], path: string[]): CategoryNode | null => {
        if (path.length === 0) return null;
        const [currentId, ...restPath] = path;

        for (const node of nodes) {
            if (node.category.id === currentId) {
                if (restPath.length === 0) {
                    return node;
                }
                return findNodeByPath(node.subcategories, restPath);
            }
        }
        return null;
    };

    const toggleCategory = async (path: string[]) => {
        const pathKey = path.join('/');
        const isExpanded = expandedCategories.has(pathKey);
        const newExpanded = new Set(expandedCategories);

        if (isExpanded) {
            newExpanded.delete(pathKey);
        } else {
            newExpanded.add(pathKey);

            setCategories((prev) => {
                const node = findNodeByPath(prev, path);
                const childrenLoaded = node && (node.subcategories.length > 0 || node.products.length > 0);

                if (!childrenLoaded && node) {
                    loadCategoryChildren(path, node.category.id);
                }

                return prev;
            });
        }

        setExpandedCategories(newExpanded);
    };

    const loadCategoryChildren = async (path: string[], categoryId: string) => {
        setCategories((prev) =>
            updateCategoryInTreeByPath(prev, path, (node) => ({
                ...node,
                isLoading: true
            }))
        );

        try {
            const membersResponse = await ProductCategoryClient.fetchProductCategoryMembers({
                group_id: categoryId
            });

            const categoryMembers = membersResponse.results.filter((m) => m.entity_type === EntityType.PRODUCT_CATEGORY);
            const productMembers = membersResponse.results.filter((m) => m.entity_type === EntityType.PRODUCT);

            const categoryIds = categoryMembers.map((m) => m.entity_id);
            const resolvedCategories = categoryIds.length > 0 ? await ProductCategoryClient.resolveProductCategories(categoryIds) : { results: [] };

            const subcategoryNodes: CategoryNode[] = await Promise.all(
                resolvedCategories.results.map(async (subcategory) => {
                    const members = await ProductCategoryClient.fetchProductCategoryMembers({
                        group_id: subcategory.id
                    });

                    const subcategoriesCount = members.results.filter((m) => m.entity_type === EntityType.PRODUCT_CATEGORY).length;
                    const productsCount = members.results.filter((m) => m.entity_type === EntityType.PRODUCT).length;

                    return {
                        category: subcategory,
                        subcategories: [],
                        products: [],
                        isLoading: false,
                        subcategoriesCount,
                        productsCount
                    };
                })
            );

            const productIds = productMembers.map((m) => m.entity_id);
            const resolvedProducts = productIds.length > 0 ? await ProductClient.resolveProducts(productIds) : { results: [] };

            setCategories((prev) =>
                updateCategoryInTreeByPath(prev, path, (node) => ({
                    ...node,
                    subcategories: subcategoryNodes,
                    products: resolvedProducts.results,
                    isLoading: false
                }))
            );
        } catch (error) {
            console.error('Error loading category children:', error);
            setCategories((prev) =>
                updateCategoryInTreeByPath(prev, path, (node) => ({
                    ...node,
                    isLoading: false
                }))
            );
        }
    };

    const updateCategoryInTreeByPath = (nodes: CategoryNode[], path: string[], updater: (node: CategoryNode) => CategoryNode): CategoryNode[] => {
        if (path.length === 0) return nodes;
        const [currentId, ...restPath] = path;

        return nodes.map((node) => {
            if (node.category.id === currentId) {
                if (restPath.length === 0) {
                    return updater(node);
                }
                return {
                    ...node,
                    subcategories: updateCategoryInTreeByPath(node.subcategories, restPath, updater)
                };
            }
            if (node.subcategories.length > 0) {
                return {
                    ...node,
                    subcategories: updateCategoryInTreeByPath(node.subcategories, path, updater)
                };
            }
            return node;
        });
    };

    const getFilteredCategories = (locale?: Locale, searchQueries?: string[]) => {
        if (!searchQueries || searchQueries.length === 0) {
            return categories;
        }

        const lowerQueries = searchQueries.map((q) => q.toLowerCase().trim()).filter((q) => q.length > 0);
        if (lowerQueries.length === 0) {
            return categories;
        }

        const matchesAnyQuery = (text: string): boolean => {
            return lowerQueries.some((query) => text.includes(query));
        };

        const categoryMatches = (category: IGroup): boolean => {
            const categoryName = category.name?.toLowerCase() || '';
            const categoryNameNl = category.name_nl_be?.toLowerCase() || '';
            const categoryNameEn = category.name_en_us?.toLowerCase() || '';
            const categoryNameFr = category.name_fr_fr?.toLowerCase() || '';
            const categoryNameDe = category.name_de_de?.toLowerCase() || '';

            return (
                matchesAnyQuery(categoryName) ||
                matchesAnyQuery(categoryNameNl) ||
                matchesAnyQuery(categoryNameEn) ||
                matchesAnyQuery(categoryNameFr) ||
                matchesAnyQuery(categoryNameDe)
            );
        };

        const productMatches = (product: IProduct): boolean => {
            const productName = product.name?.toLowerCase() || '';
            const productNameNl = product.name_nl_be?.toLowerCase() || '';
            const productNameEn = product.name_en_us?.toLowerCase() || '';
            const productNameFr = product.name_fr_fr?.toLowerCase() || '';
            const productNameDe = product.name_de_de?.toLowerCase() || '';
            const sku = product.sku?.toLowerCase() || '';
            const ean = product.ean?.toLowerCase() || '';

            return (
                matchesAnyQuery(productName) ||
                matchesAnyQuery(productNameNl) ||
                matchesAnyQuery(productNameEn) ||
                matchesAnyQuery(productNameFr) ||
                matchesAnyQuery(productNameDe) ||
                matchesAnyQuery(sku) ||
                matchesAnyQuery(ean)
            );
        };

        const markMatches = (nodes: CategoryNode[]): { matchMap: Map<string, boolean>; directMatchMap: Map<string, boolean> } => {
            const matchMap = new Map<string, boolean>();
            const directMatchMap = new Map<string, boolean>();

            const checkNode = (node: CategoryNode): boolean => {
                const nodeMatchesDirectly = categoryMatches(node.category);
                if (nodeMatchesDirectly) {
                    directMatchMap.set(node.category.id, true);
                }

                let hasMatchingSubcategory = false;
                for (const subcategory of node.subcategories) {
                    if (checkNode(subcategory)) {
                        hasMatchingSubcategory = true;
                    }
                }

                const hasMatchingProduct = node.products.some((product) => productMatches(product));

                const shouldInclude = nodeMatchesDirectly || hasMatchingSubcategory || hasMatchingProduct;
                matchMap.set(node.category.id, shouldInclude);

                return shouldInclude;
            };

            for (const node of nodes) {
                checkNode(node);
            }

            return { matchMap, directMatchMap };
        };

        const { matchMap, directMatchMap } = markMatches(categories);

        const filterCategories = (nodes: CategoryNode[]): CategoryNode[] => {
            return nodes
                .map((node) => {
                    const nodeMatches = matchMap.get(node.category.id) || false;
                    const nodeMatchesDirectly = directMatchMap.get(node.category.id) || false;

                    if (nodeMatches) {
                        if (nodeMatchesDirectly) {
                            return {
                                ...node,
                                subcategories: node.subcategories.map((sub) => {
                                    const subMatchesDirectly = directMatchMap.get(sub.category.id) || false;
                                    if (subMatchesDirectly) {
                                        return {
                                            ...sub,
                                            subcategories: sub.subcategories.map((grandSub) => ({
                                                ...grandSub,
                                                subcategories: filterCategories(grandSub.subcategories),
                                                products: grandSub.products
                                            })),
                                            products: sub.products
                                        };
                                    } else {
                                        return {
                                            ...sub,
                                            subcategories: filterCategories(sub.subcategories),
                                            products: sub.products
                                        };
                                    }
                                }),
                                products: node.products
                            };
                        } else {
                            return {
                                ...node,
                                subcategories: filterCategories(node.subcategories),
                                products: node.products.filter((product) => productMatches(product))
                            };
                        }
                    }

                    return null;
                })
                .filter((node): node is CategoryNode => node !== null);
        };

        return filterCategories(categories);
    };

    return {
        categories,
        getFilteredCategories,
        isLoading,
        toggleCategory,
        expandedCategories
    };
};
